import { c as _c } from "react/compiler-runtime";
import DropsList from "@/components/drops/view/DropsList";
import { WaveDropsReverseContainer } from "@/components/waves/drops/WaveDropsReverseContainer";
import { WaveDropsScrollControls } from "@/components/waves/drops/WaveDropsScrollControls";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject, Ref } from "react";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsMessageListSectionProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly scrollContainerCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorCallbackRef?: Ref<HTMLDivElement> | undefined;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly unreadDividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
  readonly onScrollToUnread?: ((serialNo: number) => void) | undefined;
  readonly onDismissUnread: () => void;
  readonly autoCollapseSerials?: ReadonlySet<number> | undefined;
  readonly suspendLightDropHydration?: boolean | undefined;
}
const MIN_DROPS_FOR_PAGINATION = 25;
export const WaveDropsMessageListSection: React.FC<WaveDropsMessageListSectionProps> = t0 => {
  const $ = _c(42);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    onTopIntersection,
    onReply,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    unreadDividerSerialNo,
    unreadCount,
    boostedDrops,
    onBoostedDropClick,
    onScrollToUnread,
    onDismissUnread,
    autoCollapseSerials,
    suspendLightDropHydration: t1
  } = t0;
  const suspendLightDropHydration = t1 === undefined ? false : t1;
  const hasNextPage = !!waveMessages?.hasNextPage && waveMessages.drops.length >= MIN_DROPS_FOR_PAGINATION;
  const containerRef = scrollContainerCallbackRef ?? scrollContainerRef;
  const anchorRef = bottomAnchorCallbackRef ?? bottomAnchorRef;
  const t2 = !!waveMessages?.isLoadingNextPage;
  let t3;
  if ($[0] !== waveMessages?.drops) {
    t3 = waveMessages?.drops ?? [];
    $[0] = waveMessages?.drops;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== activeDrop || $[3] !== autoCollapseSerials || $[4] !== boostedDrops || $[5] !== dropId || $[6] !== onBoostedDropClick || $[7] !== onDropContentClick || $[8] !== onQuoteClick || $[9] !== onReply || $[10] !== queueSerialTarget || $[11] !== scrollContainerRef || $[12] !== serialTarget || $[13] !== suspendLightDropHydration || $[14] !== t3 || $[15] !== targetDropRef || $[16] !== unreadDividerSerialNo) {
    t4 = <DropsList scrollContainerRef={scrollContainerRef} onReplyClick={queueSerialTarget} drops={t3} showWaveInfo={false} onReply={onReply} showReplyAndQuote={true} activeDrop={activeDrop} serialNo={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} parentContainerRef={scrollContainerRef} dropViewDropId={dropId} onDropContentClick={onDropContentClick} unreadDividerSerialNo={unreadDividerSerialNo} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={suspendLightDropHydration} key="drops-list" />;
    $[2] = activeDrop;
    $[3] = autoCollapseSerials;
    $[4] = boostedDrops;
    $[5] = dropId;
    $[6] = onBoostedDropClick;
    $[7] = onDropContentClick;
    $[8] = onQuoteClick;
    $[9] = onReply;
    $[10] = queueSerialTarget;
    $[11] = scrollContainerRef;
    $[12] = serialTarget;
    $[13] = suspendLightDropHydration;
    $[14] = t3;
    $[15] = targetDropRef;
    $[16] = unreadDividerSerialNo;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      height: "1px"
    };
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== anchorRef) {
    t6 = <div ref={anchorRef} style={t5} />;
    $[19] = anchorRef;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== bottomPaddingClassName || $[22] !== containerRef || $[23] !== hasNextPage || $[24] !== onTopIntersection || $[25] !== t2 || $[26] !== t4 || $[27] !== t6) {
    t7 = <WaveDropsReverseContainer ref={containerRef} isFetchingNextPage={t2} hasNextPage={hasNextPage} onTopIntersection={onTopIntersection} bottomPaddingClassName={bottomPaddingClassName}>{t4}{t6}</WaveDropsReverseContainer>;
    $[21] = bottomPaddingClassName;
    $[22] = containerRef;
    $[23] = hasNextPage;
    $[24] = onTopIntersection;
    $[25] = t2;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== isAtBottom || $[30] !== onDismissUnread || $[31] !== onRevealPending || $[32] !== onScrollToUnread || $[33] !== pendingCount || $[34] !== scrollContainerRef || $[35] !== scrollToBottom || $[36] !== unreadCount || $[37] !== unreadDividerSerialNo) {
    t8 = onScrollToUnread && <WaveDropsScrollControls unreadDividerSerialNo={unreadDividerSerialNo ?? null} unreadCount={unreadCount} scrollContainerRef={scrollContainerRef} onScrollToUnread={onScrollToUnread} onDismissUnread={onDismissUnread} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} newMessagesCount={pendingCount} onRevealNewMessages={onRevealPending} />;
    $[29] = isAtBottom;
    $[30] = onDismissUnread;
    $[31] = onRevealPending;
    $[32] = onScrollToUnread;
    $[33] = pendingCount;
    $[34] = scrollContainerRef;
    $[35] = scrollToBottom;
    $[36] = unreadCount;
    $[37] = unreadDividerSerialNo;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== t7 || $[40] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[39] = t7;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
};